import { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import Modal from './Modal';
import { importMembers, previewMemberImport } from '../services/ApiService';
import type { ImportMembersResult, MemberImportPreview } from '../types/models';

const EXAMPLE_CSV = 'Namn;E-post;Telefon;Fastighet;Andel;Roll\r\n'
  + 'Anna Svensson;anna@example.se;070-1234567;Berga 1:2;12,5;Styrelse\r\n'
  + 'Per Berg;per@example.se;070-7654321;Berga 1:3;12,5;Medlem\r\n';

interface MemberImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  onImported: () => void;
}

function downloadExampleFile() {
  // The byte order mark makes Excel read å/ä/ö correctly.
  const url = URL.createObjectURL(new Blob(['﻿' + EXAMPLE_CSV], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'medlemmar-exempel.csv';
  link.click();
  URL.revokeObjectURL(url);
}

function pluralMembers(count: number) {
  return count === 1 ? '1 medlem' : `${count} medlemmar`;
}

export default function MemberImportModal({ isOpen, onClose, orgId, onImported }: MemberImportModalProps) {
  const [preview, setPreview] = useState<MemberImportPreview | null>(null);
  const [result, setResult] = useState<ImportMembersResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const validRows = preview?.rows.filter(row => row.error === null) ?? [];

  const handleClose = () => {
    setPreview(null);
    setResult(null);
    setError('');
    onClose();
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      setPreview(await previewMemberImport(orgId, file));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Kunde inte läsa filen.');
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    setError('');
    setBusy(true);
    try {
      const members = validRows.map(({ rowNumber, error: rowError, ...member }) => member);
      setResult(await importMembers(orgId, members));
      onImported();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Importen misslyckades.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Importera medlemmar" size="lg">
      {error && (
        <div className="mb-4 bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">{error}</div>
      )}

      {result ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-700">{pluralMembers(result.created)} har bjudits in via e-post.</p>
          {result.alreadyMembers > 0 && (
            <p className="text-sm text-gray-500">{pluralMembers(result.alreadyMembers)} fanns redan i föreningen.</p>
          )}
          {result.notImportedDueToQuota > 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 px-4 py-2.5 rounded-xl">
              {pluralMembers(result.notImportedDueToQuota)} kunde inte bjudas in i dag eftersom dagsgränsen för utskick
              under provperioden nåddes. Importera samma fil igen i morgon – de som redan finns hoppas över.
            </p>
          )}
          <div className="flex justify-end pt-2">
            <button onClick={handleClose} className="px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm">
              Klar
            </button>
          </div>
        </div>
      ) : preview ? (
        <div className="space-y-4">
          {preview.error ? (
            <p className="text-sm text-red-700">{preview.error}</p>
          ) : (
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Rad</th>
                    <th className="px-3 py-2 font-medium">Namn</th>
                    <th className="px-3 py-2 font-medium">E-post</th>
                    <th className="px-3 py-2 font-medium">Fastighet</th>
                    <th className="px-3 py-2 font-medium">Andel</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map(row => (
                    <tr key={row.rowNumber} className={`border-t border-gray-100 ${row.error ? 'bg-red-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                      <td className="px-3 py-2">
                        {row.fullName}
                        {row.error && <p className="text-xs text-red-700 mt-0.5">{row.error}</p>}
                      </td>
                      <td className="px-3 py-2">{row.email}</td>
                      <td className="px-3 py-2">{row.propertyNumber}</td>
                      <td className="px-3 py-2">{row.sharePercentage} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-500">Rader med fel hoppas över. Alla som importeras får en inbjudan via e-post.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setPreview(null)} className="px-4 py-2 text-gray-600">Välj annan fil</button>
            <button
              onClick={handleImport}
              disabled={busy || validRows.length === 0}
              className="px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm disabled:opacity-50"
            >
              Importera {pluralMembers(validRows.length)}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Ladda upp en CSV-fil med kolumnerna <strong>Namn</strong> och <strong>E-post</strong>, och gärna Telefon,
            Fastighet, Andel och Roll (Styrelse eller Medlem). Har ni listan i Excel: välj Arkiv › Spara som › CSV.
          </p>
          <button onClick={downloadExampleFile} className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-medium">
            <Download className="w-4 h-4" />
            Ladda ner exempelfil
          </button>
          <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-8 cursor-pointer hover:bg-gray-50">
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-sm text-gray-700">{busy ? 'Läser filen…' : 'Välj CSV-fil'}</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              aria-label="Välj CSV-fil"
              disabled={busy}
              onChange={e => handleFile(e.target.files?.[0])}
            />
          </label>
        </div>
      )}
    </Modal>
  );
}
